import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import {
  deleteSeasonRateRule,
  deleteSeasonRateRuleReleaseInterval,
  fetchAdditionalServiceRulesData,
  fetchLastMinuteRuleBySeasonRateId,
  fetchMinimumLengthOfStayRuleBySeasonRateId,
  fetchPreparationRestrictionRuleBySeasonRateId,
  fetchPriceChangeRuleBySeasonRateId,
  fetchReleaseIntervalRuleBySeasonRateId,
  fetchShortStayRuleBySeasonRateId,
  updateSeasonRateAdditionalServiceRules,
  updateSeasonRateLastMinuteRule,
  updateSeasonRateMinimumLengthOfStayRule,
  updateSeasonRatePreparationRestrictionRule,
  updateSeasonRatePriceChangeRule,
  updateSeasonRateReleaseIntervalRule,
  updateSeasonRateShortStayRule,
} from '../../common/api/seasonRateRulesService';
import {
  AddonServiceRuleItemsData,
  LastMinuteRuleDetails,
  MinimumLengthOfStayRuleDetails,
  PreparationRestrictionRuleDetails,
  PriceChangeRuleDetails,
  ReleaseIntervalRuleDetails,
  ShortStayRuleDetails,
} from '../../common/types/rates.types';

export interface SeasonRateRulesState {
  minimumLengthOfStayRuleDetails: MinimumLengthOfStayRuleDetails;
  preparationRestrictionRuleDetails: PreparationRestrictionRuleDetails;
  shortStayRuleDetails: ShortStayRuleDetails;
  lastMinuteRuleDetails: LastMinuteRuleDetails;
  priceChangeRuleDetails: PriceChangeRuleDetails;
  releaseIntervalRuleDetails: ReleaseIntervalRuleDetails;
  additionalServiceRulesItemsData: AddonServiceRuleItemsData;
  minimumLengthOfStayRuleDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  preparationRestrictionRuleDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  shortStayRuleDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  lastMinuteRuleDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  priceChangeRuleDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  releaseIntervalRuleDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  additionalServiceRulesItemsDataStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
}

const initialState: SeasonRateRulesState = {
  minimumLengthOfStayRuleDetails: {
    seasonRateId: '',
    minimumLengthOfStay: null,
  },
  preparationRestrictionRuleDetails: {
    seasonRateId: '',
    preparationDays: null,
  },
  shortStayRuleDetails: {
    seasonRateId: '',
    durationRestriction: null,
    priceChange: {
      priceChangeType: 0,
      difference: 0,
      percent: 0,
    },
  },
  lastMinuteRuleDetails: {
    seasonRateId: '',
    daysUntilStart: null,
    priceChange: {
      priceChangeType: 0,
      difference: 0,
      percent: 0,
    },
  },
  priceChangeRuleDetails: {
    seasonRateId: '',
    priceChange: {
      priceChangeType: 0,
      difference: 0,
      percent: 0,
    },
  },
  releaseIntervalRuleDetails: {
    seasonRateId: '',
    daysUntilRelease: null,
    releaseLengthOfStayRule: true,
    releaseWeekdayMinimumStayRules: true,
    releaseWeekdayCheckInRules: true,
    releaseWeekdayCheckOutRules: true,
  },
  additionalServiceRulesItemsData: {
    seasonRateId: '',
    additionalServiceRules: [],
  },
  minimumLengthOfStayRuleDetailsStatus: LOADING_STATUS.IDLE,
  preparationRestrictionRuleDetailsStatus: LOADING_STATUS.IDLE,
  shortStayRuleDetailsStatus: LOADING_STATUS.IDLE,
  lastMinuteRuleDetailsStatus: LOADING_STATUS.IDLE,
  priceChangeRuleDetailsStatus: LOADING_STATUS.IDLE,
  releaseIntervalRuleDetailsStatus: LOADING_STATUS.IDLE,
  additionalServiceRulesItemsDataStatus: LOADING_STATUS.IDLE,
};

export const getSeasonRateMinimumLengthOfStayRuleAsync = createAsyncThunk(
  'seasonRateRules/getSeasonRateMinimumLengthOfStayRule',
  async (seasonRateId: string, { rejectWithValue }) => {
    try {
      const response =
        await fetchMinimumLengthOfStayRuleBySeasonRateId(seasonRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getSeasonRatePreparationRestrictionRuleAsync = createAsyncThunk(
  'seasonRateRules/getSeasonRatePreparationRestrictionRule',
  async (defaultRateId: string, { rejectWithValue }) => {
    try {
      const response =
        await fetchPreparationRestrictionRuleBySeasonRateId(defaultRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getSeasonRateShortStayRuleAsync = createAsyncThunk(
  'seasonRateRules/getSeasonRateShortStayRule',
  async (seasonRateId: string, { rejectWithValue }) => {
    try {
      const response = await fetchShortStayRuleBySeasonRateId(seasonRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getSeasonRateLastMinuteRuleAsync = createAsyncThunk(
  'seasonRateRules/getSeasonRateLastMinuteRule',
  async (seasonRateId: string, { rejectWithValue }) => {
    try {
      const response = await fetchLastMinuteRuleBySeasonRateId(seasonRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getSeasonRatePriceChangeRuleAsync = createAsyncThunk(
  'seasonRateRules/getSeasonRatePriceChangeRule',
  async (seasonRateId: string, { rejectWithValue }) => {
    try {
      const response = await fetchPriceChangeRuleBySeasonRateId(seasonRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getSeasonRateReleaseIntervalRuleAsync = createAsyncThunk(
  'seasonRateRules/getSeasonRateReleaseIntervalRule',
  async (seasonRateId: string, { rejectWithValue }) => {
    try {
      const response =
        await fetchReleaseIntervalRuleBySeasonRateId(seasonRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAdditionalServiceRulesDataAsync = createAsyncThunk(
  'seasonRateRules/getAdditionalServiceRulesDataAsync',
  async (
    {
      seasonRateId,
      accommodationId,
    }: { seasonRateId: string; accommodationId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchAdditionalServiceRulesData(
        seasonRateId,
        accommodationId,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateSeasonRateMinimumLengthOfStayRuleAsync = createAsyncThunk(
  'seasonRateRules/updateSeasonRateMinimumLengthOfStayRule',
  async (
    minimumLengthOfStayRuleData: MinimumLengthOfStayRuleDetails,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateSeasonRateMinimumLengthOfStayRule(
        minimumLengthOfStayRuleData,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateSeasonRateReleaseIntervalRuleAsync = createAsyncThunk(
  'seasonRateRules/updateSeasonRateReleaseIntervalRuleAsync',
  async (
    releaseIntervalRuleData: ReleaseIntervalRuleDetails,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateSeasonRateReleaseIntervalRule(
        releaseIntervalRuleData,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateSeasonRatePreparationRestrictionRuleAsync = createAsyncThunk(
  'seasonRateRules/updateSeasonRatePreparationRestrictionRule',
  async (
    preparationRestrictionRuleData: PreparationRestrictionRuleDetails,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateSeasonRatePreparationRestrictionRule(
        preparationRestrictionRuleData,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateSeasonRateShortStayRuleAsync = createAsyncThunk(
  'seasonRateRules/updateSeasonRateShortStayRule',
  async (shortStayRuleData: ShortStayRuleDetails, { rejectWithValue }) => {
    try {
      const response = await updateSeasonRateShortStayRule(shortStayRuleData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateSeasonRateLastMinuteRuleAsync = createAsyncThunk(
  'seasonRateRules/updateSeasonRateLastMinuteRule',
  async (lastMinuteRuleData: LastMinuteRuleDetails, { rejectWithValue }) => {
    try {
      const response = await updateSeasonRateLastMinuteRule(lastMinuteRuleData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateSeasonRatePriceChangeRuleAsync = createAsyncThunk(
  'seasonRateRules/updateSeasonRatePriceChangeRule',
  async (priceChangeRuleData: PriceChangeRuleDetails, { rejectWithValue }) => {
    try {
      const response =
        await updateSeasonRatePriceChangeRule(priceChangeRuleData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateSeasonRateAdditionalServiceRulesAsync = createAsyncThunk(
  'seasonRateRules/updateSeasonRateAdditionalServiceRules',
  async (
    addonServiceRuleItemsData: AddonServiceRuleItemsData,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateSeasonRateAdditionalServiceRules(
        addonServiceRuleItemsData,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteSeasonRateRuleAsync = createAsyncThunk(
  'seasonRateRules/deleteSeasonRateRule',
  async (ruleId: string, { rejectWithValue }) => {
    try {
      const response = await deleteSeasonRateRule(ruleId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteSeasonRateRuleReleaseIntervalAsync = createAsyncThunk(
  'seasonRateRules/deleteSeasonRateRuleReleaseInterval',
  async (ruleId: string, { rejectWithValue }) => {
    try {
      const response = await deleteSeasonRateRuleReleaseInterval(ruleId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const seasonRateRulesSlice = createSlice({
  name: 'seasonRateRules',
  initialState,
  reducers: {
    updateAdditionalServiceField: (state, action) => {
      const { index, field, value } = action.payload;
      const keys = field.split('.');
      const existingRule =
        state.additionalServiceRulesItemsData.additionalServiceRules.find(
          rule => rule.additionalServiceId === index,
        );
      if (existingRule) {
        let current = existingRule;
        for (let i = 0; i < keys.length - 1; i += 1) {
          current = current[keys[i]];
          if (!current) return;
        }
        current[keys[keys.length - 1]] = value;
      }
    },
    addAdditionalServiceRule: (state, action) => {
      state.additionalServiceRulesItemsData.additionalServiceRules.push(
        action.payload,
      );
    },
    removeAdditionalServiceRule: (state, action) => {
      const id = action.payload;
      state.additionalServiceRulesItemsData.additionalServiceRules =
        state.additionalServiceRulesItemsData.additionalServiceRules.filter(
          rule => rule.additionalServiceId !== id,
        );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(
        getSeasonRateMinimumLengthOfStayRuleAsync.pending,
        (state, action) => {
          state.minimumLengthOfStayRuleDetailsStatus = LOADING_STATUS.LOADING;
        },
      )
      .addCase(
        getSeasonRateMinimumLengthOfStayRuleAsync.fulfilled,
        (state, action) => {
          state.minimumLengthOfStayRuleDetails = action.payload;
          state.minimumLengthOfStayRuleDetailsStatus = LOADING_STATUS.IDLE;
        },
      )
      .addCase(
        getSeasonRateMinimumLengthOfStayRuleAsync.rejected,
        (state, action) => {
          state.minimumLengthOfStayRuleDetailsStatus = LOADING_STATUS.FAILED;
        },
      )
      .addCase(
        getSeasonRatePreparationRestrictionRuleAsync.pending,
        (state, action) => {
          state.preparationRestrictionRuleDetailsStatus =
            LOADING_STATUS.LOADING;
        },
      )
      .addCase(
        getSeasonRatePreparationRestrictionRuleAsync.fulfilled,
        (state, action) => {
          state.preparationRestrictionRuleDetails = action.payload;
          state.preparationRestrictionRuleDetailsStatus = LOADING_STATUS.IDLE;
        },
      )
      .addCase(
        getSeasonRatePreparationRestrictionRuleAsync.rejected,
        (state, action) => {
          state.preparationRestrictionRuleDetailsStatus = LOADING_STATUS.FAILED;
        },
      )
      .addCase(getSeasonRateShortStayRuleAsync.pending, (state, action) => {
        state.shortStayRuleDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getSeasonRateShortStayRuleAsync.fulfilled, (state, action) => {
        state.shortStayRuleDetails = action.payload;
        state.shortStayRuleDetailsStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getSeasonRateShortStayRuleAsync.rejected, (state, action) => {
        state.shortStayRuleDetailsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getSeasonRateLastMinuteRuleAsync.pending, (state, action) => {
        state.lastMinuteRuleDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getSeasonRateLastMinuteRuleAsync.fulfilled, (state, action) => {
        state.lastMinuteRuleDetails = action.payload;
        state.lastMinuteRuleDetailsStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getSeasonRateLastMinuteRuleAsync.rejected, (state, action) => {
        state.lastMinuteRuleDetailsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getSeasonRatePriceChangeRuleAsync.pending, (state, action) => {
        state.priceChangeRuleDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getSeasonRatePriceChangeRuleAsync.fulfilled, (state, action) => {
        state.priceChangeRuleDetails = action.payload;
        state.priceChangeRuleDetailsStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getSeasonRatePriceChangeRuleAsync.rejected, (state, action) => {
        state.priceChangeRuleDetailsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(
        getSeasonRateReleaseIntervalRuleAsync.pending,
        (state, action) => {
          state.releaseIntervalRuleDetailsStatus = LOADING_STATUS.LOADING;
        },
      )
      .addCase(
        getSeasonRateReleaseIntervalRuleAsync.fulfilled,
        (state, action) => {
          state.releaseIntervalRuleDetails = action.payload;
          state.releaseIntervalRuleDetailsStatus = LOADING_STATUS.IDLE;
        },
      )
      .addCase(
        getSeasonRateReleaseIntervalRuleAsync.rejected,
        (state, action) => {
          state.releaseIntervalRuleDetailsStatus = LOADING_STATUS.FAILED;
        },
      )
      .addCase(getAdditionalServiceRulesDataAsync.pending, (state, action) => {
        state.additionalServiceRulesItemsDataStatus = LOADING_STATUS.LOADING;
      })
      .addCase(
        getAdditionalServiceRulesDataAsync.fulfilled,
        (state, action) => {
          state.additionalServiceRulesItemsData = action.payload;
          state.additionalServiceRulesItemsDataStatus = LOADING_STATUS.IDLE;
        },
      )
      .addCase(getAdditionalServiceRulesDataAsync.rejected, (state, action) => {
        state.additionalServiceRulesItemsDataStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const {
  updateAdditionalServiceField,
  addAdditionalServiceRule,
  removeAdditionalServiceRule,
} = seasonRateRulesSlice.actions;

export const selectMinimumLengthOfStayRuleDetails = (state: RootState) =>
  state.seasonRateRules.minimumLengthOfStayRuleDetails;
export const selectMinimumLengthOfStayRuleDetailsLoadingStatus = (
  state: RootState,
) => state.seasonRateRules.minimumLengthOfStayRuleDetailsStatus;
export const selectPreparationRestrictionRuleDetails = (state: RootState) =>
  state.seasonRateRules.preparationRestrictionRuleDetails;
export const selectAdditionalServiceRulesItemsData = (state: RootState) =>
  state.seasonRateRules.additionalServiceRulesItemsData;
export const selectPreparationRestrictionRuleDetailsLoadingStatus = (
  state: RootState,
) => state.seasonRateRules.preparationRestrictionRuleDetailsStatus;
export const selectShortStayRuleDetails = (state: RootState) =>
  state.seasonRateRules.shortStayRuleDetails;
export const selectShortStayRuleDetailsLoadingStatus = (state: RootState) =>
  state.seasonRateRules.shortStayRuleDetailsStatus;
export const selectLastMinuteRuleDetails = (state: RootState) =>
  state.seasonRateRules.lastMinuteRuleDetails;
export const selectLastMinuteRuleDetailsLoadingStatus = (state: RootState) =>
  state.seasonRateRules.lastMinuteRuleDetailsStatus;
export const selectPriceChangeRuleDetails = (state: RootState) =>
  state.seasonRateRules.priceChangeRuleDetails;
export const selectPriceChangeRuleDetailsLoadingStatus = (state: RootState) =>
  state.seasonRateRules.priceChangeRuleDetailsStatus;
export const selectReleaseIntervalRuleDetails = (state: RootState) =>
  state.seasonRateRules.releaseIntervalRuleDetails;
export const selectReleaseIntervalRuleDetailsLoadingStatus = (
  state: RootState,
) => state.seasonRateRules.releaseIntervalRuleDetailsStatus;
export const selectAdditionalServiceRulesLoadingStatus = (state: RootState) =>
  state.seasonRateRules.additionalServiceRulesItemsDataStatus;

export default seasonRateRulesSlice.reducer;
